import {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {FOUR_SECONDS} from "../Model/Model";
import {DailyServicesWebSocket} from "./DailyServicesWebSocket";
import DailyServicesCompactRow from "../SubComponents/DailyServicesCompactRow";
import {useDailyTrigger} from "../../../../hooks/useDailyTrigger";
import {SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {clearSelfieCache} from "../../Access/SubComponents/UserAccessAvatar";
/**
 * @Author      Susana García Rodríguez
 * @JobTitle    Frontend Developer
 * @Email       sgarcia@ibl.mx
 */
const ServicesApp = ({
                         isOnline,
                         setIsOnline,
                         modified,
                         setModified,
                         option,
                         setBodyForExport,
                         item,
                         setItem,
                         handleChange
                     }) => {
    const { t } = useTranslation();
    const [activeCafeteriaOptions, setActiveCafeteriaOptions] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [now, setNow] = useState(Date.now());

    /**
     * Triggers the daily date update logic.
     *
     * <p>
     * Keeps the current item date synchronized with the
     * daily trigger behavior defined in the custom hook.
     * </p>
     */
    useDailyTrigger({ setItem: setItem });

    /**
     * Initializes the WebSocket connection and exposes
     * the manual reconnection handler.
     */
    const { reconnectWebSocket } = DailyServicesWebSocket({
        item,
        isOnline,
        setIsOnline,
        setActiveCafeteriaOptions,
        setEmployees
    });

    /**
     * Updates the current timestamp every second.
     *
     * <p>
     * This is used to evaluate whether a row should
     * be highlighted as a recent entry.
     * </p>
     */
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // The selfies are requested again the next time the module is opened //
    useEffect(() => clearSelfieCache, []);

    /**
     * Resets the modified flag when the WebSocket connection is online.
     *
     * <p>
     * This ensures the reconnect trigger is cleared once
     * the connection is restored successfully.
     * </p>
     */
    useEffect(() => {
        if (isOnline) {
            setModified(false);
        }
    }, [isOnline]);

    /**
     * Attempts to reconnect the WebSocket when the modified flag is set.
     *
     * <p>
     * Once the reconnect attempt is triggered, the modified
     * flag is reset.
     * </p>
     */
    useEffect(() => {
        if (modified) {
            reconnectWebSocket();
            setModified(false);
        }
    }, [modified]);

    /**
     * Handles cafeteria filter changes.
     *
     * <p>
     * Updates the export body and delegates the filter
     * change to the parent handler.
     * </p>
     *
     * @param {Object} e change event
     * @returns {void}
     */
    const onFilter = (e) => {
        const body = SEARCH_MODEL.setObject({
            model: option,
            modelId: e.target.value,
            key: item.search
        });

        setBodyForExport(body);
        handleChange(e);
    };

    /**
     * Renders a compact row for a delivered service record.
     *
     * @param {Object} row delivered service row data
     * @returns {JSX.Element} rendered row component
     */
    const render = (row) => {
        return <DailyServicesCompactRow row={row} t={t} />;
    };

    /**
     * Returns a stable key for a row, so a new entry at the top does not
     * shift the state of the rows below.
     *
     * <p>
     * The same foodie can be served more than once in the day,
     * so the entry date is part of the key.
     * </p>
     *
     * @param {Object} row delivered service row data
     * @returns {string} row key
     */
    const getRowKey = (row) => `${row.foodieId}-${row.entryDate}`;

    /**
     * Determines whether a row is considered recent.
     *
     * <p>
     * A row is marked as new if its entry date is within
     * the configured time window relative to the current time.
     * </p>
     *
     * @param {number|string|Date} date entry date value
     * @returns {boolean} true if the row is recent; otherwise false
     */
    const isNew = (date) => {
        const entry = new Date(date).getTime();
        return now >= entry && now <= entry + FOUR_SECONDS;
    };

    /**
     * Builds the CSS class string for a row based on
     * whether it is a recent entry.
     *
     * <p>
     * Applies left and right borders to all recent rows,
     * and top or bottom borders only when the adjacent rows
     * are not also recent. This allows contiguous recent rows
     * to appear as a single highlighted block.
     * </p>
     *
     * @param {number} index current row index
     * @param {Object} row current row data
     * @returns {string} CSS class string for the row
     */
    const getRowClassName = (index, row) => {
        if (!isNew(row?.entryDate)) return "";

        const prev = employees?.content?.[index - 1];
        const next = employees?.content?.[index + 1];

        const prevIsNew = prev && isNew(prev?.entryDate);
        const nextIsNew = next && isNew(next?.entryDate);

        return `
            border-left-secondary-150-2px
            border-right-secondary-150-2px
            ${!prevIsNew ? "border-top-secondary-150-2px" : "border-top-secondary-150"}
            ${!nextIsNew ? "border-bottom-secondary-150-2px" : "border-bottom-secondary-150"}
        `.trim();
    };

    /**
     * Handles search input changes.
     *
     * <p>
     * Updates the export body and stores the new search value
     * in the current filter item.
     * </p>
     *
     * @param {string} value search text entered by the user
     * @returns {void}
     */
    const handleSearch = (value) => {
        const body = SEARCH_MODEL.setObject({
            model: option,
            modelId: item.cafeteriaId,
            key: value
        });

        setBodyForExport(body);
        setItem({ ...item, search: value });
    };

    /**
     * Resets the local search text value.
     *
     * @returns {void}
     */
    const onReset = () => {
        setSearchText("");
    };

    return {
        t,
        activeCafeteriaOptions,
        employees,
        onFilter,
        render,
        searchText,
        setSearchText,
        getRowClassName,
        getRowKey,
        handleSearch,
        onReset
    };
};

export default ServicesApp;
